import {
  BasicModAction,
  BasicModSelector,
} from "@src/store/slices/experienceBuilder/basicMod";
import { getActiveFlowType } from "@src/store/slices/experienceBuilder/common/selectors/getActiveFlowType";
import { getDisplayLocation } from "@src/store/slices/experienceBuilder/common/selectors/getDisplayLocation";
import { getEmbeddedPreviewSrc } from "@src/store/slices/experienceBuilder/common/selectors/getEmbeddedPreviewSrc";
import { getHighlights } from "@src/store/slices/experienceBuilder/common/selectors/getHighlights";
import { getModificationsLockStatus } from "@src/store/slices/experienceBuilder/common/selectors/getModificationsLockStatus";
import { getOriginalHtmlFactory } from "@src/store/slices/experienceBuilder/common/selectors/getOriginalHtmlFactory";
import { getOriginalSrcFactory } from "@src/store/slices/experienceBuilder/common/selectors/getOriginalSrcFactory";
import { getOriginalTextFactory } from "@src/store/slices/experienceBuilder/common/selectors/getOriginalTextFactory";
import { getOtherGroupsMods } from "@src/store/slices/experienceBuilder/common/selectors/getOtherGroupsMods";
import { getPageState } from "@src/store/slices/experienceBuilder/common/selectors/getPageState";
import { getEmbeddedState } from "@src/store/slices/experienceBuilder/common/selectors/getEmbeddedState";
import { getThemes } from "@src/store/slices/experienceBuilder/common/selectors/getThemes";
import { getVariationDraft } from "@src/store/slices/experienceBuilder/common/selectors/getVariationDraft";
import { getVariationDraftErrors } from "@src/store/slices/experienceBuilder/common/selectors/getVariationDraftErrors";
import { getVariationDraftUIErrors } from "@src/store/slices/experienceBuilder/common/selectors/getVariationDraftUIErrors";
import { getVariationMods } from "@src/store/slices/experienceBuilder/common/selectors/getVariationMods";
import {
  getExperienceBasicInfo,
  getVariationById,
  getVariationNameById,
  getVisualBuilderBasePayload,
} from "@src/store/slices/experienceBuilder/common/selectors/getVisualBuilderTrackingContext";
import { hasBuilderChanges } from "@src/store/slices/experienceBuilder/common/selectors/hasBuilderChanges";
import { hasCustomTargeting } from "@src/store/slices/experienceBuilder/common/selectors/hasCustomTargeting";
import { hasVariationDraftChanges } from "@src/store/slices/experienceBuilder/common/selectors/hasVariationDraftChanges";
import { hasVariationMods } from "@src/store/slices/experienceBuilder/common/selectors/hasVariationMods";
import { isOnsiteUpsellBuilder } from "@src/store/slices/experienceBuilder/common/selectors/isOnsiteUpsellBuilder";
import { isSelectedVariationControl } from "@src/store/slices/experienceBuilder/common/selectors/isSelectedVariationControl";
import { isVariationDraftApplicable } from "@src/store/slices/experienceBuilder/common/selectors/isVariationDraftApplicable";
import { shouldShowOtherMods } from "@src/store/slices/experienceBuilder/common/selectors/shouldShowOtherMods";
import { applyDraft } from "@src/store/slices/experienceBuilder/common/thunks/applyDraft";
import { cancelDraft } from "@src/store/slices/experienceBuilder/common/thunks/cancelDraft";
import { closeBuilder } from "@src/store/slices/experienceBuilder/common/thunks/closeBuilder";
import { copyMod } from "@src/store/slices/experienceBuilder/common/thunks/copyMod";
import { deleteMod } from "@src/store/slices/experienceBuilder/common/thunks/deleteMod";
import { editMod } from "@src/store/slices/experienceBuilder/common/thunks/editMod";
import {
  trackAllModsSaved,
  trackBuilderOpened,
  trackCloseCompleted,
  trackCloseStarted,
  trackControlGroupUnlocked,
  trackDiscardModalClosed,
  trackDiscardModalViewed,
  trackElementSelected,
  trackGroupToggled,
  trackModDeleted,
  trackModEditCancelled,
  trackModEditStarted,
  trackModeToggled,
  trackModSaved,
} from "@src/store/slices/experienceBuilder/common/thunks/mixPanel";
import { openBuilder } from "@src/store/slices/experienceBuilder/common/thunks/openBuilder";
import { saveBuilder } from "@src/store/slices/experienceBuilder/common/thunks/saveBuilder";
import { setMode } from "@src/store/slices/experienceBuilder/common/thunks/setMode";
import { setDemoUrl } from "@src/store/slices/experienceBuilder/common/thunks/setDemoUrl";
import { setTargetUrl } from "@src/store/slices/experienceBuilder/common/thunks/setTargetUrl";
import { switchVariationId } from "@src/store/slices/experienceBuilder/common/thunks/switchVariationId";
import { setModsHidden } from "@src/store/slices/experienceBuilder/common/thunks/setModsHidden";
import {
  ComponentAction,
  ComponentSelector,
} from "@src/store/slices/experienceBuilder/component";
import { experienceBuilderSlice } from "@src/store/slices/experienceBuilder/slice";
import { isPageLoading } from "@src/store/slices/experienceBuilder/common/selectors/isPageLoading";
import { isPageStateDismissed } from "@src/store/slices/experienceBuilder/common/selectors/isPageStateDismissed";
import { getPluginConfigVersion } from "@src/store/slices/experienceBuilder/common/selectors/getPluginConfigVersion";

// Underscore-prefixed reducers are internal: they mutate state without the
// write-through the corresponding thunk performs. `_setDemoUrl` in particular
// would set `demoUrl` — flipping the builder into demo mode — with no
// validation and nothing persisted.
const {
  _setMode,
  _setTargetUrl,
  _setDemoUrl,
  _setModsHidden,
  ...publicActions
} = experienceBuilderSlice.actions;

export const ExperienceBuilderAction = {
  ...publicActions,
  applyDraft,
  cancelDraft,
  closeBuilder,
  copyMod,
  deleteMod,
  editMod,
  openBuilder,
  saveBuilder,
  setDemoUrl,
  setTargetUrl,
  setMode,
  setModsHidden,
  switchVariationId,
  // Tracking thunks
  trackAllModsSaved,
  trackBuilderOpened,
  trackCloseCompleted,
  trackCloseStarted,
  trackControlGroupUnlocked,
  trackDiscardModalClosed,
  trackDiscardModalViewed,
  trackElementSelected,
  trackGroupToggled,
  trackModDeleted,
  trackModEditCancelled,
  trackModEditStarted,
  trackModSaved,
  trackModeToggled,
  basicMod: BasicModAction,
  component: ComponentAction,
};

export const ExperienceBuilderSelector = {
  ...experienceBuilderSlice.selectors,
  getActiveFlowType,
  getDisplayLocation,
  getEmbeddedPreviewSrc,
  getModificationsLockStatus,
  shouldShowOtherMods,
  getHighlights,
  getOriginalHtmlFactory,
  getOriginalTextFactory,
  getOriginalSrcFactory,
  getOtherGroupsMods,
  getVariationMods,
  getPageState,
  getPluginConfigVersion,
  getEmbeddedState,
  getThemes,
  getVariationDraft,
  getVariationDraftErrors,
  getVariationDraftUIErrors,
  hasVariationDraftChanges,
  hasCustomTargeting,
  hasVariationMods,
  isPageLoading,
  isPageStateDismissed,
  isOnsiteUpsellBuilder,
  isVariationDraftApplicable,
  hasBuilderChanges,
  isSelectedVariationControl,
  // Tracking context selectors
  getVisualBuilderBasePayload,
  getExperienceBasicInfo,
  getVariationNameById,
  getVariationById,
  // BasicMod flow
  basicMod: BasicModSelector,
  // Component flow
  component: ComponentSelector,
};
